import { HttpClient } from '@angular/common/http';
import { Injectable, SecurityContext } from '@angular/core';
import { User } from '@formbird/types';
import { DocumentUpdater, ESQueryUtils, RulesetError, SearchResultsProcessor, SharedUrlRoutes, UtilDocumentId } from '@formbird/shared';
import { cloneDeep, isFunction } from 'lodash';
import { NotificationService } from '../notification/notification.service';
import { ClientIncludeRuleSetService } from './client-include-rule-set.service';
import { Observable } from 'rxjs';
import { DataService } from '../data/data.service';
import { LazyScriptLoaderService } from '../lazyload/lazy-script-loader.service';
import { LoggedInUserService } from '../user/logged-in-user.service';
import { DocumentService } from '../document/document.service';
import { PhoneService } from '../phone/phone.service';
import { OfflineStatusService } from '../offline-status/offline-status.service';
import { SearchService } from '../search/search.service';
import { ModifiedFieldService } from '../document/modified-field.service';
import { DomSanitizer } from '@angular/platform-browser';
import { select } from '../../redux/decorators/select';

const logger = console;

@Injectable({
  providedIn: 'root'
})
export class ClientRulesService {

  @select(['userState', 'user']) user$: Observable<User>;
  user: User;
  modalService: any;

  constructor(
    private dataService: DataService,
    private loggedInUserService: LoggedInUserService,
    private clientIncludeRuleSetService: ClientIncludeRuleSetService,
    private documentService: DocumentService,
    private phoneService: PhoneService,
    private offlineStatusService: OfflineStatusService,
    private http: HttpClient,
    private lazyScriptLoaderService: LazyScriptLoaderService,
    private searchService: SearchService,
    private notificationService: NotificationService,
    private modifiedFieldService: ModifiedFieldService,
    private sanitizer: DomSanitizer
  ) {
    this.user$.subscribe((data: User) => this.user = data);
  }

  private sharedValues = {} as any;

  async expose() {

    return {
      user: this.user,

      sharedValues: this.sharedValues,

      modalService: this.modalService,

      dataService: this.dataService,

      DataService: this.dataService,  // used in rules for the old code convention

      ModifiedFieldService: this.modifiedFieldService,

      lazyLoader: this.lazyScriptLoaderService,

      loggedInUserService: this.loggedInUserService,

      clientIncludeRuleSetService: this.clientIncludeRuleSetService,

      documentService: this.documentService,

      phoneService: this.phoneService,

      offlineStatusService: this.offlineStatusService,

      http: this.http,

      searchService: this.searchService,

      notificationService: this.notificationService,

      q: Promise,

      logger: logger,

      ModalService: this.modalService,

      PhoneService: this.phoneService,

      loadRecords: function(collectionName, callback) {
        logger.error('LoadRecords regards collectionName is not valid function.');
      },

      sanitizeHtml: function (html: string) {
        return this.sanitizer.sanitize(SecurityContext.HTML, html);
      },

      hasDuplicate: this.hasDuplicate,

      showNotification: this.showNotification,

      /**
       * returns a boolean indicating whether the device is connected
       */
      isOnline: this.offlineStatusService.isConnected,

      updateDocument: this.updateDocument,

      deleteDocument: this.deleteDocument,

      findDocuments: this.findDocuments,

      findDocumentsByElastic: this.findDocumentsByElastic,

      findDocumentsBySearch: this.findDocumentsBySearch,

      sendMessageBirdSMS: this.sendMessageBirdSMS,

      /*
       * Including ruleset processing.
       * Include ruleset syntax: #include 'rulesetName';
       */
      addIncludesToRuleset: this.addIncludesToRuleset,

      createDocumentFromTemplate: this.createDocumentFromTemplate,

      loadComponentLibrary: this.loadComponentLibrary,

      setSharedValue: this.setSharedValue,

      getSharedValue: this.getSharedValue,

      RulesetError: RulesetError,

      processSearchResults: SearchResultsProcessor.processSearchResults,

      showDialog: this.showDialog
    };
  }

  private hasDuplicate(templateId, duplicateCondition, IdToExclude) {
    const _self = this;

    const query = ESQueryUtils.prepareESQuery(duplicateCondition);
    query.query.bool.must.push({ 'systemHeader.templateId': templateId });

    // Execute query
    return new Promise((resolve, reject) => {

      _self.dataService.findByElastic(query).then(
        function successFunc(results) {

          // Check excluded ID
          let result = false;

          for (let i = 0; i < results.data.hits.total; i++) {

            if (results.data.hits.hits[i]._source.documentId !== IdToExclude) {
              result = true;
              break;
            }

          }

          resolve(result);
        },

        function errorFunc(err) {
          reject(err);
        }
      );

    });

  }

  private showNotification(message, status) {
    if (!status) {
      status = 'info';
    }
    status = status.toLowerCase();
    this.notificationService.printMessage(message, status, null, { x: 'right', y: 'top' });
  }

  private updateDocument(id, change, userId, callback) {

    const _self = this;

    function convertToErrorObject(error){
      let err = error;
      if (typeof err === 'string' ){
        err = new Error(err);
      }

      return err;
    }

    function doUpdateDoc(orgDoc) {
      try {
          const clonedDoc = cloneDeep(orgDoc);

          DocumentUpdater.applyUpdatesSimple(clonedDoc, change);

          _self.dataService.jsonPatchUpdate(orgDoc, clonedDoc).then(
            function successFunc(doc) {
              callback(null, doc);
            },
            function errorFunc(err) {
              const errObj = convertToErrorObject(err);
              callback(errObj);
            }
          );
      } catch (error){
        const errObj = convertToErrorObject(error);
        callback(errObj);
      }
    }

    const originalDoc = _self.documentService.getExistingDocument(id);
    if (!originalDoc) {

      _self.dataService.getDocument(id).then(
        function successFunc(orgDoc) {
          doUpdateDoc(orgDoc);
        },
        function errorFunc(err) {
          callback(err);
        }
      );

    } else {
      doUpdateDoc(originalDoc);
    }

  }

  private deleteDocument(id, options, callback) {

    const _self = this;

    if (isFunction(options)) {
      callback = options;
      options = null;
    }

    if (id) {

      _self.dataService.destroy(id).then(
        function successFunc(doc) {
          callback();
        },
        function errorFunc(err) {
          callback(err);
        }
      );
    }
  }

  private findDocuments(query, userId, callback) {
    logger.warn('===== Deprecated function: findDocuments. Use findDocumentsByElastic instead. =====');
    logger.warn('Passing userId to findDocuments is ignored. The logged in user will be used');

    const queryString = '?q=' + JSON.stringify(query);
    const url = SharedUrlRoutes.serverRoutes.loadDocsForRulesBaseUrl + '/' + userId + queryString;

    this.http.get(url).subscribe(
      (response) => {
        callback(null, response);

      },
      (err) => {
        callback(err);
      });
  }

  private sendMessageBirdSMS(targetNum, message, callback) {

    const url = SharedUrlRoutes.serverRoutes.sendSMS;
    const params = {
      targetNum: targetNum,
      message: message
    };

    this.http.post(url, params).subscribe(
      (response) => {
        callback(null, response);
      },
      (err) => {
        callback(err);
      });
  }

  addIncludesToRuleset(ruleFlow, callback) {
    this.clientIncludeRuleSetService.addIncludesToRuleset(ruleFlow, callback);
  }

  private createDocumentFromTemplate(docInfo, templateId, userId, callback) {

    const loggedInUserId = this.user.account.documentId;

    if (userId && userId !== loggedInUserId) {
      logger.warn('Logged in user id will be used rather than the user id parameter ' +
        'passed to createDocumentFromTemplate');
    }

    if (!docInfo.documentId) {
      docInfo.documentId = UtilDocumentId.generateId();
    }

    if (!docInfo.systemHeader) {
      docInfo.systemHeader = {};
    }
    docInfo.systemHeader.templateId = templateId;

    this.dataService.insert(docInfo).then(
      function successFunc(result) {
        callback(null, result);
      },
      function errorFunc(err) {
        callback(err);
      }
    );
  }

  private loadComponentLibrary(serviceName, callback) {

    const url = SharedUrlRoutes.serverRoutes.loadComponentLibrary + '/' + serviceName;

    let srv: any;
    try {
      // srv = this.$injector.get(ComponentLoader.getComponentLibraryScriptServiceName(serviceName));
    } catch (err) {
    }

    if (srv) {
      callback(null, srv);

    } else {
      callback(null, srv);
      // this.lazyLoader.load([{ type: 'js', path: url, serie: true }]).then(
      //   function (success) {

      //     srv = this.$injector.get(ComponentLoader.getComponentLibraryScriptServiceName(serviceName));

      //     if (srv) {
      //       callback(null, srv);
      //     } else {
      //       callback('Component library not found');
      //     }

      //   },
      //   function (error) {
      //     callback(error);
      //   });
    }

  }

  private setSharedValue(key, value) {
    this.sharedValues[key] = value;
  }

  private getSharedValue(key) {
    return this.sharedValues[key];
  }

  findDocumentsBySearch(query, userId, searchContext, callback) {

    const _self = this;

    if (isFunction(searchContext)) {
      callback = searchContext;
      searchContext = {};
    }

    if (userId) {
      logger.warn('Passing userId to findDocumentsBySearch is ignored. The logged in user will be used');
    }

    const options = {
      filter: query
    };

    this.searchService.search(options, searchContext).then(
      function successFunc(data) {
        callback(null, data);
      },
      function errorFunc(err) {
        _self.notificationService.printMessage(err.message, 'error');
        callback(err);
      }
    );


  }

  findDocumentsByElastic(query, userId, searchContext, callback) {

    if (isFunction(searchContext)) {
      callback = searchContext;
      searchContext = {};
    }

    if (userId) {
      logger.warn('Passing userId to findDocumentsByElastic is ignored. The logged in user will be used');
    }

    this.dataService.findByElastic(query, searchContext).then(
      function successFunc(data) {

        // Need to refactor the result because of its requirement is the same as findDocuments
        callback(null, data);
      },

      function errorFunc(err) {
        callback(err);
      }
    );
  }

  showDialog(message, errorType, succFunc, errorFunc, discardFunc) {

    this.modalService.showDialog(message, errorType, succFunc, errorFunc, discardFunc);

  }

  /** set the modal service. This is set from the application importing the @formbird/services
   *  library so that a native app can set its own implementation of ModalService
   */
  setModalService(modalService: any) {
    this.modalService = modalService;
  }
}
